'use client';
import { ArrowSquareRight, ChevronDown, SearchLg, Trash01 } from '@ethsign/icons';
import { Button, Dropdown, Input } from '@ethsign/ui';
import { debounce } from 'lodash-es';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Table } from '@/components';

import { statusToText } from '@/components/StatusTag';
import { GlobalConfig } from '@/constants';
import { usePageVisibility } from '@/hooks';
import { globalVm } from '@/pages/GlobalPageCtrl';
import { Contracts, ContractsQuery } from '@/types';

interface Props {
  columns: any;
  data: Contracts;
  onBatchMove?: (selectedData: any[] | undefined) => void;
  onBatchHide?: (selectedData: any[] | undefined) => void;
  rowSelection?: { rowSelected: readonly any[] };
  onQuery: (data: ContractsQuery) => void;
  contractsQuery?: ContractsQuery;
  folderId?: string;
}
const contractTypeToText = {
  invite: 'Sent by Invitation',
  link: 'Sent by Link'
};

export function ContractTable(props: Props) {
  const vmData = globalVm.$useSnapshot();
  const { savedParamsActive } = vmData;
  const { folderId, columns, data, onBatchMove, onBatchHide, rowSelection, onQuery, ...res } = props;
  const currentData = data.data;
  const tableRef = useRef<any>(null);
  const [rowSelected, setRowSelected] = useState<any>([]);
  const [queryInfo, setQueryInfo] = useState<ContractsQuery>({
    keyword: '',
    filter: '',
    page: 1,
    contractType: undefined
  });
  const queryRef = useRef(queryInfo);

  const onSearch = (params?: any, update = true) => {
    if (params && update) {
      setQueryInfo(params);
    }
    onQuery?.(params || queryInfo);
  };

  usePageVisibility(() => {
    console.log('show');
    onSearch?.();
  });

  queryRef.current = queryInfo;

  useEffect(() => {
    if (savedParamsActive) {
      const value = sessionStorage.getItem(GlobalConfig.SessionKeyOfDashboardSearch);
      console.log(value, 'value');
      if (value) {
        const params = JSON.parse(value);
        onSearch?.(params);
      } else {
        onSearch();
      }
    } else {
      onSearch();
    }
  }, [savedParamsActive]);

  useEffect(() => {
    return () => {
      // 缓存搜索条件
      sessionStorage.setItem(
        GlobalConfig.SessionKeyOfDashboardSearch,
        JSON.stringify({ ...queryRef.current, folderId })
      );
      globalVm.setSavedParamsActive(false);
    };
  }, []);

  const handleChange = useCallback(
    debounce((e, queryInfo) => {
      const params = {
        ...queryInfo,
        keyword: e.target.value,
        page: 1
      };
      onSearch(params, false);
    }, 1000),
    []
  );

  useEffect(() => {
    setRowSelected(rowSelection?.rowSelected);
    if (!(rowSelection?.rowSelected && rowSelection.rowSelected.length > 0)) {
      tableRef.current?.toggleAllPageRowsSelected(false);
    }
  }, [rowSelection?.rowSelected]);
  const currentPage = data.page || 1;

  return (
    <div className="border border-gray-200 rounded-lg  overflow-hidden">
      <div className="flex flex-row justify-between items-center h-[68px] px-4">
        {rowSelected?.length > 0 ? (
          <div>
            <Button
              variant="outline"
              className="mr-3"
              onClick={() => {
                onBatchMove && onBatchMove(rowSelected);
              }}
            >
              <span className="mr-2">
                <ArrowSquareRight color="#344054" size={20} />
              </span>
              Move to
            </Button>
            <Button
              variant="destructive"
              className="mr-3"
              onClick={() => {
                onBatchHide && onBatchHide(rowSelected);
              }}
            >
              <span className="mr-2">
                <Trash01 size={20} color="#ffffff" />
              </span>
              Move to Trash
            </Button>
          </div>
        ) : (
          <div>
            <Dropdown
              items={['', ...Object.keys(statusToText)].map((key) => {
                const text = statusToText[key as keyof typeof statusToText] || 'View All';
                return {
                  icon: undefined,
                  label: text,
                  onClick: () => {
                    onSearch({ ...queryInfo, filter: key, page: 1 });
                  }
                };
              })}
            >
              <div className="border border-gray-300 rounded-lg flex flex-row items-center">
                <div className="text-sm font-normal  text-gray-800 px-4 py-[10px] border-r border-gray-300">
                  {statusToText[queryInfo.filter as keyof typeof statusToText] || 'Status: View All'}
                </div>
                <div className="px-3 py-[10px]">
                  <ChevronDown size={20} color="#667085" />
                </div>
              </div>
            </Dropdown>
            <Dropdown
              items={['', ...Object.keys(contractTypeToText)].map((key) => {
                const text = contractTypeToText[key as keyof typeof contractTypeToText] || 'View All';
                return {
                  icon: undefined,
                  label: text,
                  onClick: () => {
                    onSearch({ ...queryInfo, contractType: key, page: 1 });
                  }
                };
              })}
            >
              <div className="border border-gray-300 rounded-lg flex flex-row items-center ml-6">
                <div className="text-sm font-normal  text-gray-800 px-4 py-[10px] border-r border-gray-300">
                  {contractTypeToText[queryInfo.contractType as keyof typeof contractTypeToText] ||
                    'Contract Type: View All'}
                </div>
                <div className="px-3 py-[10px]">
                  <ChevronDown size={20} color="#667085" />
                </div>
              </div>
            </Dropdown>
          </div>
        )}
        <div className="flex flex-row items-center">
          <div className="w-[400px] mt-[-6px]">
            <Input
              id="search"
              prefix={<SearchLg size={20} color="#667085"></SearchLg>}
              placeholder="Contract name or sender address"
              onChange={(e: any) => {
                handleChange(e, queryInfo);
                setQueryInfo({
                  ...queryInfo,
                  keyword: e.target.value
                });
              }}
              value={queryInfo.keyword}
            ></Input>
          </div>
        </div>
      </div>
      <Table
        emptyText={
          <div className={'py-10'}>
            <div className="text-gray-900 font-medium text-md">There&apos;s nothing here 😴</div>
            <div className="text-gray-500 text-sm mt-[4px] text-center">Upload and send your first contract!</div>
          </div>
        }
        pagination={{
          pageSize: 6,
          pageIndex: currentPage - 1,
          total: data.total || 0,
          manualPagination: true,
          onChange(page) {
            if (currentPage !== page) onSearch({ ...queryInfo, page });
          }
        }}
        getTable={(table) => (tableRef.current = table)}
        rowSelection={
          rowSelection
            ? {
                onRowSelect: (selectedObject) => {
                  setRowSelected(
                    data?.data?.filter((_: any, i: number) => Object.keys(selectedObject).includes(i + ''))
                  );
                }
              }
            : undefined
        }
        columns={columns}
        data={(currentData as any) || []}
        {...res}
      />
    </div>
  );
}
